import { ComponentType } from 'react'

// This file will be populated as we migrate pages
// For now, we'll create placeholder components and add real ones as we migrate

/**
 * Route Registry
 * Maps database route paths to React components
 * This must include ALL possible routes that can come from the database
 */
// Import migrated pages
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import TwoFactor from '@/pages/TwoFactor'
import TwoFactorSetup from '@/pages/TwoFactor/Setup'
import Profile from '@/pages/Profile'
import TestCSPPage from '@/pages/TestCSP'
import TaskList from '@/pages/Helpdesk/TaskList'
import IncomingRequest from '@/pages/IncomingRequest'
import Acknowledged from '@/pages/Helpdesk/Acknowledged'
import ParentTicketList from '@/pages/Helpdesk/ParentTicketList'
import ParentTicketCreationPage from '@/pages/Helpdesk/ParentTicketCreation'
import ParentTicketsListPage from '@/pages/ParentTickets/index'
import ParentTicketCreatePage from '@/pages/ParentTickets/Create'
import ParentTicketEditPage from '@/pages/ParentTickets/Edit'
import ParentTicketViewPage from '@/pages/ParentTickets/View'
import ServiceTypesListPage from '@/pages/ServiceTypes/index'
import ServiceTypeCreatePage from '@/pages/ServiceTypes/Create'
import ServiceTypeEditPage from '@/pages/ServiceTypes/Edit'
import ParentTicketStatusesListPage from '@/pages/ParentTicketStatuses/index'
import ParentTicketStatusCreatePage from '@/pages/ParentTicketStatuses/Create'
import ParentTicketStatusEditPage from '@/pages/ParentTicketStatuses/Edit'
import ChildTicketStatusesListPage from '@/pages/ChildTicketStatuses/index'
import ChildTicketStatusCreatePage from '@/pages/ChildTicketStatuses/Create'
import ChildTicketStatusEditPage from '@/pages/ChildTicketStatuses/Edit'
import SlaTimingListPage from '@/pages/SlaTiming/index'
import SlaTimingCreatePage from '@/pages/SlaTiming/Create'
import SlaTimingEditPage from '@/pages/SlaTiming/Edit'
import AgentStatusPage from '@/pages/AgentStatus/index'
import MailboxInboxPage from '@/pages/MailboxInbox'

export const routeRegistry: Record<string, ComponentType<any>> = {
  // Public routes
  '/login': Login,
  '/forgot-password': ForgotPassword,
  '/reset-password': ResetPassword,
  '/two-factor': TwoFactor,
  '/two-factor/setup': TwoFactorSetup,

  // Dashboard
  '/dashboard': Dashboard,
  // Other
  '/profile': Profile,
  '/test-csp': TestCSPPage,

  // Helpdesk (dashboard uses main Dashboard with ticket report)
  '/incoming-request': IncomingRequest,
  '/helpdesk/dashboard': Dashboard,

  // Parent Tickets (canonical routes)
  '/tickets': ParentTicketsListPage,
  '/tickets/create': ParentTicketCreatePage,
  '/tickets/:id/edit': ParentTicketEditPage,
  '/tickets/:id/view': ParentTicketViewPage,
  '/tickets/:id': ParentTicketEditPage,

  // Service Catalog - Service Types (align with backend user_routes: list, :id = edit, create)
  '/service-catalog/service-types': ServiceTypesListPage,
  '/service-catalog/service-types/create': ServiceTypeCreatePage,
  '/service-catalog/service-types/:id': ServiceTypeEditPage,

  // Service Catalog - Parent Ticket Statuses
  '/service-catalog/parent-ticket-statuses': ParentTicketStatusesListPage,
  '/service-catalog/parent-ticket-statuses/create': ParentTicketStatusCreatePage,
  '/service-catalog/parent-ticket-statuses/:id': ParentTicketStatusEditPage,

  // Service Catalog - Child Ticket Statuses
  '/service-catalog/child-ticket-statuses': ChildTicketStatusesListPage,
  '/service-catalog/child-ticket-statuses/create': ChildTicketStatusCreatePage,
  '/service-catalog/child-ticket-statuses/:id': ChildTicketStatusEditPage,

  // Service Catalog - SLA & Timing
  '/service-catalog/sla-timing': SlaTimingListPage,
  '/service-catalog/sla-timing/create': SlaTimingCreatePage,
  '/service-catalog/sla-timing/:id': SlaTimingEditPage,

  // Agent Status (team members reporting to current user)
  '/agent-status': AgentStatusPage,

  // Microsoft Graph mailbox (ingested emails)
  '/mailbox-inbox': MailboxInboxPage,
}

/**
 * Get component for a route path
 * Handles dynamic segments (e.g., :id, :userId)
 */
export function getRouteComponent(path: string): ComponentType<any> | null {
  // Normalize: registry keys use leading slash and single slashes
  const normalized =
    '/' + (path || '').replace(/^\/+/, '').replace(/\/+/g, '/').replace(/\/+$/, '') || '/'

  // Direct match
  if (routeRegistry[normalized]) {
    return routeRegistry[normalized]
  }

  // Try to match dynamic routes
  // e.g., "/categories/123" matches "/categories/:id"
  const dynamicPatterns = Object.keys(routeRegistry).filter((key) =>
    key.includes(':'),
  )

  for (const pattern of dynamicPatterns) {
    const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$')
    if (regex.test(normalized)) {
      return routeRegistry[pattern]
    }
  }

  return null
}
